import React, { useState, useEffect, useCallback } from "react";
import { 
  Heart, Search, Download, Building2, Package, User, 
  UserX, Calendar, CreditCard, TrendingUp, LayoutDashboard, 
  Filter, ChevronLeft, ChevronRight 
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import * as XLSX from 'xlsx'; // Import SheetJS
import SummaryApi from "../common";
import toast from "react-hot-toast";

const SakhiReport = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  
  // --- Pagination State ---
  const [currentPage, setCurrentPage] = useState(1);
  const ordersPerPage = 5;

  const fetchAllSakhiOrders = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(SummaryApi.getAllSakhiOrders.url, {
        method: SummaryApi.getAllSakhiOrders.method,
      });
      const result = await response.json();
      if (result.status) {
        setOrders(result.data);
      }
    } catch (error) {
      toast.error("Failed to fetch Sakhi reports");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchAllSakhiOrders();
  }, [fetchAllSakhiOrders]);

  // --- Excel Export Logic ---
  const handleExportExcel = () => {
    if (orders.length === 0) return toast.error("No data to export");

    // Map data to a clean format for Excel
    const dataToExport = orders.map(order => ({
      "Order Date": new Date(order.order_date_time).toLocaleDateString(),
      "Order Code": order.order_code,
      "Sponsor Name": order.user_name,
      "Mobile": order.mobile_number || "Anonymous",
      "Plan Name": order.plan_name || "Impact Plan",
      "Pads Quantity": order.pads_quantity || 0,
      "Amount Paid": order.total_amount,
      "Distribution Mode": order.sakhi_through_mode.toUpperCase(),
      "Status": order.order_status,
      "Address": order.order_address
    }));

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sakhi Sponsorships");

    // Download file
    XLSX.writeFile(workbook, `Sakhi_Impact_Report_${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success("Excel report downloaded!");
  };

  // --- Filtering & Pagination Logic ---
  const filteredOrders = orders.filter(order => 
    order.user_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    order.order_code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.ceil(filteredOrders.length / ordersPerPage);
  const indexOfLastOrder = currentPage * ordersPerPage;
  const indexOfFirstOrder = indexOfLastOrder - ordersPerPage;
  const currentOrders = filteredOrders.slice(indexOfFirstOrder, indexOfLastOrder);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // --- Calculations for Stats ---
  const totalContribution = orders.reduce((acc, curr) => acc + parseFloat(curr.total_amount), 0);
  const totalSakhis = orders.length;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFF0F3] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-purple-800 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-purple-900 font-black uppercase tracking-widest text-[10px]">Syncing Data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#FFF0F3] min-h-screen font-sans text-slate-900 p-6 md:p-12">
      <div className="max-w-7xl mx-auto">
        
        {/* --- Header --- */}
        <header className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-12">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-100 text-purple-800 text-[10px] font-black mb-3 uppercase tracking-wider">
              <LayoutDashboard size={12} /> Sakhi Internal Control
            </div>
            <h1 className="text-4xl font-black text-purple-950 uppercase tracking-tighter">
              Sponsorship <span className="text-pink-600 italic">Ledger</span>
            </h1>
          </div>
          
          <button 
            onClick={handleExportExcel}
            className="bg-purple-950 text-white px-8 py-4 rounded-2xl font-black text-xs uppercase flex items-center justify-center gap-3 hover:bg-black transition-all shadow-2xl shadow-purple-200 active:scale-95"
          >
            <Download size={18} /> Export to Excel
          </button>
        </header>

        {/* --- Stats Summary --- */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          <StatCard title="Total Impact Fund" value={`₹${totalContribution.toLocaleString()}`} icon={<CreditCard />} color="purple" />
          <StatCard title="Active Sakhis" value={totalSakhis} icon={<Heart />} color="pink" />
          <StatCard title="Project Reach" value={orders.filter(o => o.sakhi_through_mode === 'eveya').length} icon={<Building2 />} color="teal" />
        </div>

        {/* --- Filter & Search --- */}
        <div className="bg-white rounded-[2.5rem] p-4 mb-8 shadow-xl shadow-purple-100 flex flex-col md:flex-row gap-4 items-center border border-purple-50">
          <div className="relative flex-1 w-full">
            <Search className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
            <input 
              type="text" 
              placeholder="Search by Sponsor Name or Order Code..."
              className="w-full pl-14 pr-6 py-4 rounded-2xl bg-slate-50 border-none focus:ring-2 focus:ring-purple-500 outline-none font-medium transition-all"
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
            />
          </div>
          <div className="bg-slate-100 px-6 py-4 rounded-2xl text-slate-500 font-bold text-xs uppercase flex items-center gap-2">
            <Filter size={16} /> Filters
          </div>
        </div>

        {/* --- Paginated List --- */}
        <div className="space-y-4 mb-10">
          <AnimatePresence mode="popLayout">
            {currentOrders.map((order, index) => (
              <motion.div 
                layout
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                key={order.order_id} 
                className="bg-white rounded-[2rem] p-6 md:p-8 flex flex-col lg:flex-row items-center gap-8 shadow-sm hover:shadow-xl transition-all border border-purple-50 group"
              >
                <div className="flex items-center gap-5 lg:w-1/4 w-full">
                  <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shrink-0 shadow-inner ${order.user_id === 0 ? 'bg-slate-100 text-slate-300' : 'bg-pink-50 text-pink-600'}`}>
                    {order.user_id === 0 ? <UserX size={28} /> : <User size={28} />}
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-black text-purple-950 uppercase truncate leading-tight group-hover:text-pink-600 transition-colors">{order.user_name}</h3>
                    <p className="text-[10px] font-bold text-slate-400 tracking-widest uppercase">{order.mobile_number || 'Confidential'}</p>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-10 flex-1 w-full text-center lg:text-left">
                  <div className="min-w-[100px]">
                    <p className="text-[9px] font-black text-slate-300 uppercase mb-1">Impact Date</p>
                    <p className="text-sm font-bold text-slate-600">{new Date(order.order_date_time).toLocaleDateString('en-GB')}</p>
                  </div>
                  <div className="min-w-[120px]">
                    <p className="text-[9px] font-black text-slate-300 uppercase mb-1">Order Code</p>
                    <p className="text-sm font-black text-purple-900">{order.order_code}</p>
                  </div>
                  <div>
                    <p className="text-[9px] font-black text-slate-300 uppercase mb-1">Contribution</p>
                    <p className="text-xl font-black text-purple-900 tracking-tighter">₹{order.total_amount}</p>
                  </div>
                  <div className="min-w-[140px]">
                    <p className="text-[9px] font-black text-slate-300 uppercase mb-1">Route</p>
                    <span className={`px-4 py-1.5 rounded-xl text-[9px] font-black uppercase tracking-widest inline-flex items-center gap-2 ${order.sakhi_through_mode === 'eveya' ? 'bg-teal-50 text-teal-700' : 'bg-orange-50 text-orange-700'}`}>
                      {order.sakhi_through_mode === 'eveya' ? <Building2 size={12}/> : <Package size={12}/>}
                      {order.sakhi_through_mode}
                    </span>
                  </div>
                </div>

                <div className="lg:w-1/6 w-full text-center lg:text-right">
                    <p className="text-[9px] font-black text-slate-300 uppercase mb-1">Status</p>
                    <div className="flex items-center lg:justify-end gap-1.5">
                        <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
                        <span className="text-xs font-black text-slate-700 uppercase">{order.order_status}</span>
                    </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>

          {filteredOrders.length === 0 && (
            <div className="text-center py-24 bg-white rounded-[3rem] border-2 border-dashed border-pink-100">
              <UserX size={48} className="mx-auto text-pink-200 mb-4" />
              <p className="text-slate-400 font-bold uppercase tracking-widest text-xs">No sponsorship records match your query</p>
            </div>
          )}
        </div>

        {/* --- Pagination Controls --- */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-3 pb-10">
            <button 
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="w-12 h-12 rounded-2xl bg-white border border-purple-100 flex items-center justify-center text-purple-900 hover:bg-purple-900 hover:text-white transition-all disabled:opacity-30 shadow-lg shadow-purple-100"
            >
              <ChevronLeft size={20} />
            </button>
            
            {[...Array(totalPages)].map((_, i) => (
              <button
                key={i}
                onClick={() => handlePageChange(i + 1)}
                className={`w-12 h-12 rounded-2xl font-black text-sm transition-all shadow-lg ${currentPage === i + 1 ? 'bg-purple-900 text-white shadow-purple-200' : 'bg-white border border-purple-100 text-purple-900 shadow-purple-100'}`}
              >
                {i + 1}
              </button>
            ))}

            <button 
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="w-12 h-12 rounded-2xl bg-white border border-purple-100 flex items-center justify-center text-purple-900 hover:bg-purple-900 hover:text-white transition-all disabled:opacity-30 shadow-lg shadow-purple-100"
            >
              <ChevronRight size={20} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

/* --- Stat Card --- */
const StatCard = ({ title, value, icon, color }) => {
  const colors = {
    purple: "bg-purple-950 shadow-purple-200",
    pink: "bg-pink-600 shadow-pink-200",
    teal: "bg-teal-700 shadow-teal-200",
  };

  return (
    <div className={`p-8 rounded-[2.5rem] text-white shadow-2xl ${colors[color]} relative overflow-hidden group`}>
      <div className="absolute -right-6 -bottom-6 opacity-10 group-hover:scale-125 transition-transform duration-700">
        {React.cloneElement(icon, { size: 120 })}
      </div>
      <div className="relative z-10">
        <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center mb-4">
          {React.cloneElement(icon, { size: 20 })}
        </div>
        <p className="text-[10px] font-black uppercase tracking-widest opacity-60 mb-1">{title}</p>
        <p className="text-3xl font-black tracking-tighter">{value}</p>
      </div>
    </div>
  );
};

export default SakhiReport;